<script lang="ts">
  import type { Snippet } from "svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiAlertOutline, mdiCheck, mdiArrowRight } from "@mdi/js"
  import LearningDisclosure from "../../../../_shared/_modules/learning-disclosure"
  import GuideSourceText from "./_modules/guide-source-text"
  import type {
    GuideBasicBlock,
    GuideBlock,
    GuideChoice,
    GuideImage,
  } from "../../../../../../../../../../api/kehrblatt/learning-guide"

  const { blocks, choices, selected, onChoose, renderImage } = $props<{
    blocks: GuideBlock[]
    choices: GuideChoice[]
    selected: Record<string, string>
    onChoose: (choiceKey: string, optionKey: string) => void
    renderImage: Snippet<[GuideImage]>
  }>()
  const inlineChoices = $derived(
    new Set(blocks.filter((block) => block.kind === "choice").map((block) => block.choice_key)),
  )
  let editingChoices = $state<Record<string, boolean>>({})

  function precedingInstructions(end: number): number {
    return blocks.slice(0, end).reduce((count, block) => {
      if (block.kind === "instruction") return count + 1

      if (block.kind === "branch") {
        const visible = block.cases.find((item) => item.option_key === selected[block.choice_key])

        return count + (visible?.blocks.filter((item) => item.kind === "instruction").length ?? 0)
      }

      return count
    }, 0)
  }
</script>

{#snippet choiceSelector(choice: GuideChoice, compact = false)}
  {@const option = choice.options.find((option) => option.key === selected[choice.key])}
  {@const showSelect = !compact || !option || editingChoices[choice.key]}
  {@const programChoice = choice.key === "programm" || choice.key === "listenformat"}
  {@const label = programChoice ? "Ihr Kehrbezirksprogramm" : choice.title}
  <div class="path-choice">
    {#if compact && option}
      <div class="chosen-path">
        <p>{programChoice ? "Ihr Programm" : "Ihre Auswahl"}: <strong>{option.label}</strong></p>
        <button
          type="button"
          aria-expanded={Boolean(showSelect)}
          aria-controls={`guide-choice-${choice.key}`}
          aria-label={`${showSelect ? "Auswahl schließen" : "Ändern"}: ${label}`}
          onclick={() => (editingChoices[choice.key] = !showSelect)}
        >
          {showSelect ? "Auswahl schließen" : "Ändern"}
        </button>
      </div>
    {/if}
    <div id={`guide-choice-${choice.key}`} hidden={!showSelect}>
      <label>
        <span>{label}</span>
        <select
          value={selected[choice.key] ?? ""}
          onchange={(event) => {
            // Keep a newly revealed select mounted while it owns keyboard focus.
            if (compact) editingChoices[choice.key] = true

            onChoose(choice.key, event.currentTarget.value)
          }}
        >
          <option value="" disabled
            >{programChoice ? "Programm auswählen" : "Bitte auswählen"}</option
          >
          {#each choice.options as option (option.key)}
            <option value={option.key}>{option.label}</option>
          {/each}
        </select>
      </label>
      {#if option?.description}<p class="choice-description">{option.description}</p>{/if}
    </div>
  </div>
{/snippet}

{#snippet basicContent(block: GuideBasicBlock, instructionNumber: number)}
  {#if block.kind === "instruction"}
    <section class="instruction">
      <h3><span class="action-number" aria-hidden="true">{instructionNumber}</span>{block.title}</h3
      >
      <p>{block.text}</p>
      {#each block.images as image, imageIndex (`${imageIndex}:${image.src}`)}
        {@render renderImage(image)}
      {/each}
    </section>
  {:else if block.kind === "notice"}
    <aside class="notice" class:warning={block.tone === "warning"}>
      {#if block.tone === "warning"}<Icon path={mdiAlertOutline} size="1.3rem" />{/if}
      <div><h3>{block.title}</h3><p>{block.text}</p></div>
    </aside>
  {:else if block.kind === "disclosure"}
    <LearningDisclosure title={block.title}>
      <p class="disclosure-text"><GuideSourceText text={block.text} /></p>
      {#each block.images as image, imageIndex (`${imageIndex}:${image.src}`)}
        {@render renderImage(image)}
      {/each}
    </LearningDisclosure>
  {:else if block.kind === "comparison"}
    <section class="comparison">
      <h3>{block.title}</h3>
      <div class="comparison-columns">
        {#each block.items as item, index (index)}
          <div><h4>{item.title}</h4><p>{item.text}</p></div>
        {/each}
      </div>
    </section>
  {:else if block.kind === "sequence"}
    <section class="sequence">
      <h3>{block.title}</h3>
      <ol>
        {#each block.items as item, index (index)}
          <li
            ><span class="sequence-number">{index + 1}</span><div
              ><h4>{item.title}</h4><p>{item.text}</p></div
            >
            {#if index < block.items.length - 1}<span class="sequence-arrow" aria-hidden="true"
                ><Icon path={mdiArrowRight} /></span
              >{/if}
          </li>
        {/each}
      </ol>
    </section>
  {:else if block.kind === "checklist"}
    <section class="checklist">
      <h3>{block.title}</h3>
      <ul
        >{#each block.items as item, index (index)}<li
            ><Icon path={mdiCheck} size="1.1rem" /><span>{item}</span></li
          >{/each}</ul
      >
    </section>
  {/if}
{/snippet}

<div class="stage-blocks">
  {#each blocks as block, index (index)}
    {#if block.kind === "choice"}
      {@const choice = choices.find((choice) => choice.key === block.choice_key)}
      {#if choice}{@render choiceSelector(choice)}{/if}
    {:else if block.kind === "branch"}
      {@const choice = choices.find((choice) => choice.key === block.choice_key)}
      {@const branch = block.cases.find((item) => item.option_key === selected[block.choice_key])}
      {#if choice && !inlineChoices.has(choice.key)}{@render choiceSelector(choice, true)}{/if}
      {#if branch}
        <div class="selected-path" aria-live="polite">
          {#each branch.blocks as item, branchIndex (`${selected[block.choice_key]}:${branchIndex}`)}
            {@render basicContent(
              item,
              precedingInstructions(index) +
                branch.blocks
                  .slice(0, branchIndex + 1)
                  .filter((item) => item.kind === "instruction").length,
            )}
          {/each}
        </div>
      {:else}
        <p class="choose-prompt"
          >Wählen Sie oben im Feld die passende Möglichkeit. Danach erscheinen die zugehörigen Schritte.</p
        >
      {/if}
    {:else}
      {@render basicContent(block, precedingInstructions(index) + 1)}
    {/if}
  {/each}
</div>

<style>
  .stage-blocks,
  .selected-path {
    display: grid;
    gap: 1.75rem;
    min-width: 0;
  }
  h3,
  h4,
  p {
    margin: 0;
    color: inherit;
  }
  h3 {
    font-size: 19px;
    line-height: 1.45;
    margin-bottom: 0.65rem;
  }
  h4 {
    font-size: 18px;
    line-height: 1.45;
    margin-bottom: 0.4rem;
  }
  p,
  li {
    font-size: 18px;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
  p {
    max-width: 72ch;
    white-space: pre-line;
  }
  .instruction > h3 {
    display: flex;
    align-items: baseline;
    gap: 0.65rem;
  }
  .instruction > p {
    margin-bottom: 1rem;
  }
  .action-number {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 1.65rem;
    height: 1.65rem;
    flex: 0 0 1.65rem;
    background: #344e5a;
    color: #fff;
    font-size: 15px;
  }
  .path-choice {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  .path-choice label {
    display: grid;
    gap: 0.5rem;
    max-width: 36rem;
    font-size: 18px;
    font-weight: 600;
  }
  .chosen-path {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.65rem;
    margin-bottom: 0.5rem;
  }
  .chosen-path p {
    font-size: 16px;
  }
  .chosen-path button {
    background: transparent;
    border: 0;
    padding: 0.35rem 0;
    font: inherit;
    font-size: 16px;
    color: #176288;
    text-decoration: underline;
    text-underline-offset: 0.2em;
    cursor: pointer;
  }
  select {
    appearance: auto;
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 44px;
    margin: 0;
    padding: 0.6rem 0.7rem;
    border: 1px solid #8b9ea7;
    border-radius: 2px;
    background: white;
    color: inherit;
    font: inherit;
    font-size: 18px;
    font-weight: 400;
  }
  select:focus-visible,
  .chosen-path button:focus-visible {
    outline: 2px solid #176288;
    outline-offset: 3px;
  }
  .path-choice [hidden] {
    display: none;
  }
  .choice-description {
    margin-top: 0.35rem;
    font-size: 16px;
    line-height: 1.45;
  }
  .choose-prompt {
    border-left: 3px solid #7c2024;
    padding: 0.5rem 1rem;
  }
  .notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    border-left: 3px solid #176288;
    background: #f0f6f8;
  }
  .notice.warning {
    border-color: #a55c00;
    background: #fff7e7;
    color: #63400c;
  }
  .notice h3 {
    margin-bottom: 0.3rem;
  }
  .disclosure-text {
    margin-bottom: 0.75rem;
  }
  .comparison-columns {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr));
    gap: 1rem;
  }
  .comparison-columns > div {
    border-left: 3px solid #bfcbd0;
    padding-left: 1rem;
  }
  .comparison-columns > div:nth-child(2) {
    border-color: #7c2024;
  }
  .comparison-columns > div:nth-child(3) {
    border-color: #367653;
  }
  .sequence ol {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
  .sequence li {
    display: flex;
    gap: 0.65rem;
    flex: 1 1 180px;
    min-width: 0;
  }
  .sequence-number {
    color: #7c2024;
    font-weight: 700;
  }
  .sequence-arrow {
    padding-top: 0.2rem;
  }
  .checklist ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 0.6rem;
  }
  .checklist li {
    display: flex;
    align-items: baseline;
    gap: 0.65rem;
    max-width: 72ch;
  }
</style>
